import { ArrowDown, ArrowUp } from 'lucide-react';
import { formatDollars } from '@/lib/useRetailStores';
import {
  dataThroughNote,
  formatUnits,
  sellerTrend,
  useHomeTopSellers,
  type HomeFilters,
  type TopSeller,
} from '@/lib/useHomeDashboard';
import { TileMessage, TileShell, TileSkeleton } from './TileShell';
import { EXCLUDED_ITEMS_NOTE, MUTED_TEXT, NOT_AVAILABLE } from './tileText';

function TrendCell({ item }: { item: TopSeller }) {
  const t = sellerTrend(item.units, item.units_ly);
  if (t.direction === 'flat') {
    return <span className={`w-[64px] text-right text-[13px] ${MUTED_TEXT}`}>flat</span>;
  }
  const up = t.direction === 'up';
  const pct = t.isNew || t.pct == null ? 'new' : `${Math.abs(t.pct * 100).toFixed(0)}%`;
  return (
    <span
      className={`inline-flex w-[64px] items-center justify-end gap-0.5 text-[13px] font-semibold ${
        up ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
      }`}
    >
      {up ? <ArrowUp aria-hidden size={13} /> : <ArrowDown aria-hidden size={13} />}
      {pct}
    </span>
  );
}

export function TopSellers({ filters }: { filters: HomeFilters }) {
  const { data, isLoading, unavailable } = useHomeTopSellers(filters);
  const items = data?.items ?? [];
  const max = items.reduce((m, i) => Math.max(m, i.units), 0);
  const through = dataThroughNote(data?.range ?? null);
  const caption = `Units sold, vs. the same days last year${through ? `. ${through}` : ''}`;

  return (
    <TileShell label="Items" title="Top sellers" caption={caption}>
      {isLoading ? (
        <TileSkeleton rows={6} />
      ) : unavailable ? (
        <TileMessage>{NOT_AVAILABLE}</TileMessage>
      ) : items.length === 0 ? (
        <TileMessage>No sales in this period yet.</TileMessage>
      ) : (
        <ol className="flex flex-col gap-1.5">
          {items.map((item, idx) => {
            const name = item.descr ?? item.item_no;
            const tip = `${name}: ${formatUnits(item.units)} units, ${formatDollars(item.net_sales)} net sales`;
            return (
              <li key={item.item_no} className="flex items-center gap-2" title={tip} aria-label={tip}>
                <span className={`w-5 text-right text-[13px] tabular-nums ${MUTED_TEXT}`}>{idx + 1}</span>
                <span className="w-[40%] truncate text-[13px] text-amp-ink dark:text-amp-dark-ink sm:w-[36%]">
                  {name}
                </span>
                <div className="h-3 flex-1 rounded bg-amp-cream dark:bg-amp-dark-surface-warm">
                  <div
                    className="h-full rounded bg-blue-600 dark:bg-blue-400"
                    style={{ width: `${max > 0 ? (item.units / max) * 100 : 0}%` }}
                  />
                </div>
                <span className="w-[48px] text-right text-[13px] tabular-nums text-amp-ink dark:text-amp-dark-ink">
                  {formatUnits(item.units)}
                </span>
                <TrendCell item={item} />
              </li>
            );
          })}
        </ol>
      )}
      <p className={`mt-3 text-[12px] ${MUTED_TEXT}`}>{EXCLUDED_ITEMS_NOTE}</p>
    </TileShell>
  );
}
